import { api } from "@/lib/api";
import { format, parseISO } from "date-fns";
import { callDateKey, monthKey, ymd } from "./period";

const KEY = "minutepay_monthly_goal";
const RECORD_KEY = "minutepay_monthly_goal_record";
const MAP_KEY = "minutepay_monthly_goals";
const MIGRATED_KEY = "minutepay_goals_migrated";

function monthFromDateLike(value, fallback) {
  if (!value) return fallback;
  const text = String(value);
  if (/^\d{4}-\d{2}$/.test(text)) return text;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 7);
  return fallback;
}

function parseAmount(value) {
  if (value && typeof value === "object") return Number(value.amount);
  return Number(value);
}

function readMap() {
  const nowMonth = monthKey(new Date());
  const map = {};

  try {
    const raw = localStorage.getItem(MAP_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const fallback = parseAmount(parsed.default);
      Object.entries(parsed).forEach(([key, value]) => {
        if (key === "default") return;
        if (!/^\d{4}-\d{2}$/.test(key)) return;
        const amount = parseAmount(value);
        if (Number.isFinite(amount) && amount > 0) map[key] = amount;
      });
      if (Number.isFinite(fallback) && fallback > 0 && !map[nowMonth]) {
        map[nowMonth] = fallback;
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const raw = localStorage.getItem(RECORD_KEY);
    if (raw) {
      const rec = JSON.parse(raw);
      const amount = Number(rec?.amount);
      if (Number.isFinite(amount) && amount > 0) {
        const recMonth = monthFromDateLike(rec.createdAt || rec.month, nowMonth);
        if (!map[recMonth]) map[recMonth] = amount;
      }
    }
  } catch {
    /* ignore */
  }

  const single = Number(localStorage.getItem(KEY));
  if (Number.isFinite(single) && single > 0 && Object.keys(map).length === 0) {
    map[nowMonth] = single;
  }

  return map;
}

function persistMap(map) {
  localStorage.setItem(MAP_KEY, JSON.stringify(map));
  localStorage.setItem(KEY, String(map[monthKey(new Date())] || 0));
}

export function listGoalMonths() {
  return Object.keys(readMap()).sort().reverse();
}

export function nextGoalsHydrateAction(cloud, local, migrated) {
  const cloudMap = cloud && typeof cloud === "object" && !Array.isArray(cloud) ? cloud : {};
  const localMap = local && typeof local === "object" && !Array.isArray(local) ? local : {};
  const cloudHas = Object.values(cloudMap).some((value) => Number(value) > 0);
  const localHas = Object.values(localMap).some((value) => Number(value) > 0);
  if (cloudHas) return { action: "use_cloud", goals: cloudMap };
  if (!migrated && localHas) return { action: "migrate_local", goals: localMap };
  if (migrated) return { action: "use_cloud", goals: {} };
  return { action: "keep_local", goals: localMap };
}

export async function hydrateGoalsFromServer() {
  const local = readMap();
  const migrated = localStorage.getItem(MIGRATED_KEY) === "1";
  let cloud = {};
  try {
    const res = await api.get("/goals/monthly");
    cloud = res.data?.goals || {};
  } catch {
    return local;
  }
  const next = nextGoalsHydrateAction(cloud, local, migrated);
  if (next.action === "migrate_local") {
    try {
      const res = await api.put("/goals/monthly", { goals: next.goals });
      persistMap(res.data?.goals || next.goals);
      localStorage.setItem(MIGRATED_KEY, "1");
      return res.data?.goals || next.goals;
    } catch {
      return local;
    }
  }
  persistMap(next.goals);
  if (next.action === "use_cloud") localStorage.setItem(MIGRATED_KEY, "1");
  return next.goals;
}

export function getMonthlyGoal(month = monthKey(new Date())) {
  const amount = Number(readMap()[month]) || 0;
  return amount > 0 ? amount : 0;
}

export function getGoalRecord(month = monthKey(new Date())) {
  return { amount: getMonthlyGoal(month), month, createdAt: null };
}

export function setMonthlyGoal(amount, month = monthKey(new Date())) {
  const value = Math.max(0, Number(amount) || 0);
  const map = readMap();
  if (value <= 0) delete map[month];
  else map[month] = value;
  persistMap(map);
  return value;
}

export async function saveMonthlyGoal(amount, month = monthKey(new Date())) {
  const value = Math.max(0, Number(amount) || 0);
  const res = await api.put(`/goals/monthly/${month}`, { amount: value });
  const saved = Number(res.data?.amount) || 0;
  const map = readMap();
  if (saved > 0) map[month] = saved;
  else delete map[month];
  persistMap(map);
  localStorage.setItem(MIGRATED_KEY, "1");
  return saved;
}

export function remainingEligibleDays(month, now = new Date()) {
  const start = parseISO(`${month}-01T12:00:00`);
  if (Number.isNaN(start.getTime())) return 0;
  const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const current = monthKey(now);
  if (month < current) return 0;
  if (month > current) return daysInMonth;
  return Math.max(0, daysInMonth - now.getDate() + 1);
}

export function monthEarnings(calls, month) {
  return (calls || []).reduce((sum, call) => {
    const key = callDateKey(call);
    if (!key || !key.startsWith(month)) return sum;
    return sum + (Number(call.earnings) || 0);
  }, 0);
}

export function monthGoalSnapshot(calls, month = monthKey(new Date()), now = new Date(), amountOverride) {
  const start = parseISO(`${month}-01T12:00:00`);
  const daysInMonth = Number.isNaN(start.getTime())
    ? 0
    : new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const goal = amountOverride != null ? Math.max(0, Number(amountOverride) || 0) : getMonthlyGoal(month);
  const earned = monthEarnings(calls, month);
  const remaining = Math.max(goal - earned, 0);
  const remainingDays = remainingEligibleDays(month, now);
  const requiredDailyPace = remainingDays > 0 ? remaining / remainingDays : 0;
  const progressPct = goal > 0 ? (earned / goal) * 100 : 0;
  const todayKey = ymd(now);
  const todayEarnings = month === monthKey(now)
    ? (calls || []).reduce((sum, call) => (
      callDateKey(call) === todayKey ? sum + (Number(call.earnings) || 0) : sum
    ), 0)
    : 0;
  const reached = goal > 0 && earned >= goal;
  const past = month < monthKey(now);

  return {
    month,
    monthLabel: Number.isNaN(start.getTime()) ? month : format(start, "MMMM yyyy"),
    daysInMonth,
    dayOfMonth: month === monthKey(now) ? now.getDate() : (past ? daysInMonth : 1),
    remainingDays,
    goal,
    earned,
    remaining,
    progressPct,
    dailyTarget: requiredDailyPace,
    requiredDailyPace,
    todayEarnings,
    reached,
    past,
    ahead: goal > 0 && remainingDays > 0 ? todayEarnings >= requiredDailyPace - 0.005 : null,
    paceDelta: todayEarnings - requiredDailyPace,
    paceMessage: null,
    hint: remainingDays > 0
      ? `${formatMoney(requiredDailyPace)}/day across ${remainingDays} remaining day${remainingDays === 1 ? "" : "s"} would close the gap.`
      : reached
        ? "Target reached for this month."
        : "This month has ended.",
  };
}

export function goalPacing(calls, record, now = new Date()) {
  const month = record?.month || monthKey(now);
  return monthGoalSnapshot(calls, month, now, record?.amount);
}

export function goalHistory(calls, now = new Date()) {
  return listGoalMonths().map((month) => {
    const snap = monthGoalSnapshot(calls, month, now);
    return {
      month: snap.month,
      monthLabel: snap.monthLabel,
      goal: snap.goal,
      earned: snap.earned,
      progressPct: snap.progressPct,
      reached: snap.reached,
    };
  });
}

function formatMoney(n) {
  return `$${(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
